import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { updateTokens } from "../../redux/features/auth/authSlice";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import {
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  Box,
  Chip,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import HOC from "../../components/HOC";
import LocationAssign from "../../components/common/LocationAssign";
import { LuMapPin } from "react-icons/lu";
import {
  createUpdateLocation,
  getDetailMyLocation,
  getListMyLocation,
} from "../../services/profileApi";
import { getDetailLocation } from "../../services/purchaseApi";
import { IoArrowBack, IoInformationCircleOutline } from "react-icons/io5";
import { useNotification } from "../../components/context/NotificationContext";

const textFieldStyles = {
  backgroundColor: "white",
  borderRadius: "10px",
  color: "#111827",
  transition: "all 0.4s",
  fontFamily: '"Montserrat", sans-serif',
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
    borderColor: "#d1d5db",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#9ca3af",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#111827",
    borderWidth: "1px",
  },
  "& .MuiInputBase-input": {
    padding: "14px 12px",
    color: "#111827",
    fontSize: { xs: "14px", lg: "16px" },
    fontFamily: '"Montserrat", sans-serif',
    "&::placeholder": {
      color: "#CBCBCB",
      opacity: 1,
      fontFamily: '"Montserrat", sans-serif',
    },
  },
  "&.Mui-disabled": {
    backgroundColor: "#f9fafb",
    opacity: 0.7,
  },
};

const errorTextFieldStyles = {
  ...textFieldStyles,
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
    borderColor: "#ef4444",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ef4444",
  },
};

const formHelperFieldStyles = {
  width: "100%",
  display: "flex",
  justifyContent: "start",
  margin: "2px 0 0 0",
  fontSize: "12px",
  color: "#ef4444",
  fontFamily: '"Montserrat", sans-serif',
};

const getCleanPhoneNumber = (formatted) => {
  return formatted.replace(/[^\d+]/g, "");
};

const validationSchema = yup.object({
  id: yup.number(),
  user_name: yup.string().required("Nama lengkap wajib diisi"),
  phone_number: yup
    .string()
    .required("Nomor ponsel wajib diisi")
    .test(
      "phone-validation",
      "Format nomor ponsel tidak valid (min. 10 digit)",
      function (value) {
        if (!value) return false;
        const cleanPhone = getCleanPhoneNumber(value);
        const phoneRegex = /^\+628\d{9,13}$/;
        return phoneRegex.test(cleanPhone);
      },
    ),
  address: yup.string().required(),
  detail_address: yup.string().required("Alamat lengkap wajib diisi"),
  latitude: yup.number().required(),
  longitude: yup.number().required(),
  province_name: yup.string().required("Provinsi wajib diisi"),
  city_name: yup.string().required("Kabupaten / Kota wajib diisi"),
  district_name: yup.string().required("Kecamatan wajib diisi"),
  sub_district_name: yup.string().required("Kelurahan wajib diisi"),
  postal_code: yup.string().required("Kode pos wajib diisi"),
  geography_id: yup.number().required().min(1, "Kode pos wajib dipilih"),
  is_priority: yup.boolean(),
  notes: yup.string(),
});

const AddNewAddress = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const companyId = localStorage.getItem("companyId");
  const { id } = useParams();
  const location = useLocation();
  const isNew = location.pathname.includes("/new");
  const { showNotification } = useNotification();

  const [step, setStep] = useState(1);
  const [isStepTransitioning, setIsStepTransitioning] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [reqBodyLocation, setReqBodyLocation] = useState({
    search: "",
    type: 1,
    limit: 0,
    offset: 0,
    keyword: "",
  });
  const [locationData, setLocationData] = useState({
    provinces: [],
    cities: [],
    districts: [],
    subDistricts: [],
  });
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);

  const getFieldStyles = (hasError) => {
    return hasError ? errorTextFieldStyles : textFieldStyles;
  };

  const handleProvinceChange = (_, newValue) => {
    const provinceName = newValue ? newValue.name : "";

    formik.setFieldValue("province_name", provinceName);
    formik.setFieldValue("city_name", "");
    formik.setFieldValue("district_name", "");
    formik.setFieldValue("sub_district_name", "");
    formik.setFieldValue("postal_code", "");
    formik.setFieldValue("geography_id", 0);

    setReqBodyLocation({
      search: "",
      type: 2,
      limit: 0,
      offset: 0,
      keyword: provinceName,
    });
  };

  const handleCityChange = (_, newValue) => {
    const cityName = newValue ? newValue.name : "";

    formik.setFieldValue("city_name", cityName);
    formik.setFieldValue("district_name", "");
    formik.setFieldValue("sub_district_name", "");
    formik.setFieldValue("postal_code", "");
    formik.setFieldValue("geography_id", 0);

    setReqBodyLocation({
      search: "",
      type: 3,
      limit: 0,
      offset: 0,
      keyword: cityName,
    });
  };

  const handleDistrictChange = (_, newValue) => {
    const districtName = newValue ? newValue.name : "";

    formik.setFieldValue("district_name", districtName);
    formik.setFieldValue("sub_district_name", "");
    formik.setFieldValue("postal_code", "");
    formik.setFieldValue("geography_id", 0);

    setReqBodyLocation({
      search: "",
      type: 4,
      limit: 0,
      offset: 0,
      keyword: districtName,
    });
  };

  const handleSubDistrictChange = (_, newValue) => {
    const subDistrictName = newValue ? newValue.name : "";
    const postalCodeValue = newValue ? newValue.kode_pos : "";
    const geographyId = newValue ? newValue.id : 0;

    formik.setFieldValue("sub_district_name", subDistrictName);
    formik.setFieldValue("postal_code", postalCodeValue);
    formik.setFieldValue("geography_id", geographyId);
  };

  const handlePostalCodeChange = (_, newValue) => {
    const selectedKodePos = newValue ? newValue.name : "";
    const selectedLocation = newValue ? newValue : null;

    formik.setFieldValue("postal_code", selectedKodePos);
    formik.setFieldValue(
      "geography_id",
      selectedLocation ? selectedLocation.id : 0,
    );
  };

  const { data: dataDetail } = useQuery({
    queryKey: ["detail-my-location", id],
    queryFn: () => getDetailMyLocation(Number(id)),
    enabled: !!id,
  });

  const { data: listLocations } = useQuery({
    queryKey: ["list-my-location"],
    queryFn: () => getListMyLocation(),
  });

  const hasPriorityLocation = !!listLocations?.some(
    (loc) => loc.is_priority === true,
  );
  const isEditMode = Boolean(id);

  const [initialIsPriority, setInitialIsPriority] = useState(true);

  useEffect(() => {
    if (!isEditMode && listLocations) {
      const hasPriority = listLocations.some((loc) => loc.is_priority === true);
      setInitialIsPriority(!hasPriority);
    }
  }, [isEditMode, listLocations]);

  useEffect(() => {
    if (!dataDetail || !isEditMode) return;

    setLocationData((prev) => ({
      ...prev,
      provinces:
        prev.provinces && prev.provinces.length > 0
          ? prev.provinces
          : dataDetail.province
            ? [{ id: dataDetail.geography_id || 0, name: dataDetail.province }]
            : prev.provinces,
    }));
  }, [dataDetail, isEditMode]);

  useEffect(() => {
    if (!dataDetail || !isEditMode) return;

    const base = { search: "", limit: 0, offset: 0 };

    const fetchEditLocationLists = async () => {
      try {
        const [cities, districts, subDistricts] = await Promise.all([
          dataDetail.province
            ? getDetailLocation({
                ...base,
                type: 2,
                keyword: dataDetail.province,
              })
            : Promise.resolve([]),
          dataDetail.city
            ? getDetailLocation({ ...base, type: 3, keyword: dataDetail.city })
            : Promise.resolve([]),
          dataDetail.district
            ? getDetailLocation({
                ...base,
                type: 4,
                keyword: dataDetail.district,
              })
            : Promise.resolve([]),
        ]);

        setLocationData((prev) => ({
          ...prev,
          cities: cities || [],
          districts: districts || [],
          subDistricts: subDistricts || [],
        }));
      } catch (err) {
        console.error("[fetchEditLocationLists] failed:", err);
      }
    };

    fetchEditLocationLists();
  }, [dataDetail, isEditMode]);

  const { mutate: mutateGetLocation } = useMutation({
    mutationFn: getDetailLocation,
    onSuccess: (data) => {
      setLocationData((prev) => {
        switch (reqBodyLocation.type) {
          case 1:
            return { ...prev, provinces: data || [] };
          case 2:
            return { ...prev, cities: data || [] };
          case 3:
            return { ...prev, districts: data || [] };
          case 4:
            return {
              ...prev,
              subDistricts: data || [],
            };
          default:
            return prev;
        }
      });
    },
  });

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/[^\d+]/g, "");

    if (cleaned.startsWith("+62")) {
      const numbers = cleaned.substring(3);
      const limitedNumbers = numbers.substring(0, 13);

      return "(+62) " + limitedNumbers;
    }

    return value;
  };

  const handlePhoneChange = (value) => {
    let cleaned = value.replace(/[^\d]/g, "");

    if (cleaned.startsWith("0")) {
      cleaned = "62" + cleaned.substring(1);
    } else if (cleaned.startsWith("8")) {
      cleaned = "62" + cleaned;
    } else if (!cleaned.startsWith("62")) {
      cleaned = "62" + cleaned;
    }

    cleaned = cleaned.substring(0, 15);

    const formatted = formatPhoneNumber("+" + cleaned);
    formik.setFieldValue("phone_number", formatted);
    setErrorMessage("");
  };

  const formik = useFormik({
    initialValues: {
      location_id: dataDetail ? dataDetail.location_id : 0,
      user_name: dataDetail ? dataDetail.username : "",
      phone_number: dataDetail ? dataDetail.phone_number : "",
      address: dataDetail ? dataDetail.address : "",
      detail_address: dataDetail ? dataDetail.detail_address : "",
      latitude: dataDetail ? dataDetail.latitude : 0,
      longitude: dataDetail ? dataDetail.longitude : 0,
      is_priority: dataDetail ? dataDetail.is_priority : initialIsPriority,
      notes: dataDetail ? dataDetail.notes : "",
      geography_id:
        dataDetail && dataDetail.geography_id != null
          ? dataDetail.geography_id
          : 0,
      province_name: dataDetail ? dataDetail.province || "" : "",
      city_name: dataDetail ? dataDetail.city || "" : "",
      district_name: dataDetail ? dataDetail.district || "" : "",
      sub_district_name: dataDetail ? dataDetail.sub_district || "" : "",
      postal_code: dataDetail ? dataDetail.kode_pos || "" : "",
      company_id: 0,
    },
    onSubmit: (value) => {
      const submitData = {
        location_id: value.location_id,
        user_name: value.user_name,
        phone_number: getCleanPhoneNumber(value.phone_number),
        address: value.address,
        detail_address: value.detail_address,
        latitude: value.latitude,
        longitude: value.longitude,
        is_priority: value.is_priority,
        notes: value.notes,
        geography_id: value.geography_id,
        company_id: Number(companyId),
      };
      mutateCreateUpdate(submitData);
    },
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const noteSuggestions = [
    "Chat setelah sampai",
    "Bel",
    "Titip pos satpam",
    "Taruh depan pagar",
    "Telp setelah tiba",
    "Titip tetangga",
  ];

  const handleSuggestionClick = (suggestion) => {
    formik.setFieldValue("notes", suggestion);
    setSelectedSuggestion(suggestion);
  };

  const handleNotesChange = (e) => {
    if (selectedSuggestion && e.target.value !== selectedSuggestion) {
      setSelectedSuggestion(null);
    }
    formik.setFieldValue("notes", e.target.value);
  };

  const { mutate: mutateCreateUpdate, isPending } = useMutation({
    mutationFn: createUpdateLocation,
    onSuccess: (tokenData) => {
      if (tokenData) {
        dispatch(
          updateTokens({
            accessToken: tokenData.access,
            accessExpired: tokenData.access_expired,
            renewToken: tokenData.renew,
            renewExpired: tokenData.renew_expired,
          }),
        );
      }
      navigate("/location-address/my-address");
      queryClient.invalidateQueries({
        queryKey: ["list-my-location"],
      });
      showNotification(
        `Berhasil ${isNew ? "membuat alamat baru" : "mengubah alamat"}`,
        "success",
      );
    },
    onError: (err) => {
      setErrorMessage(err.message.id || "Terjadi kesalahan");
      showNotification(err.message.id || "Terjadi kesalahan", "error");
    },
  });

  useEffect(() => {
    if (reqBodyLocation) {
      mutateGetLocation(reqBodyLocation);
    }
  }, [reqBodyLocation, mutateGetLocation]);

  const step1Fields = [
    "user_name",
    "phone_number",
    "detail_address",
    "province_name",
    "city_name",
    "district_name",
    "sub_district_name",
    "postal_code",
    "geography_id",
  ];
  const isStep1Valid = step1Fields.every((field) => !formik.errors[field]);
  const isStep2Valid =
    formik.values.latitude !== 0 && formik.values.longitude !== 0;

  const handleGoToStep2 = () => {
    setIsStepTransitioning(true);
    setStep(2);
    setTimeout(() => setIsStepTransitioning(false), 400);
  };

  return (
    <div className="w-full md:mt-4 min-h-screen">
      <div className="flex justify-center min-h-screen">
        <div className="w-full max-w-7xl mx-auto md:px-5 lg:px-0 text-base">
          <div className="hidden md:block mb-6 mt-4">
            <nav className="text-base text-slate-600 mb-3 flex flex-wrap items-center gap-1 font-medium">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="hover:text-slate-900 hover:underline transition-colors"
              >
                Beranda
              </button>
              <span className="text-slate-400">/</span>
              <button
                type="button"
                onClick={() => navigate("/location-address")}
                className="hover:text-slate-900 hover:underline transition-colors"
              >
                Lokasi & Pengiriman
              </button>
              <span className="text-slate-400">/</span>
              <button
                type="button"
                onClick={() => navigate("/location-address/my-address")}
                className="hover:text-slate-900 hover:underline transition-colors"
              >
                List Alamat
              </button>
              <span className="text-slate-400">/</span>
              <span className="text-primary">
                {isNew ? "Tambah Alamat" : "Edit Alamat"}
              </span>
            </nav>

            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-2xl lg:text-3xl font-semibold text-slate-800 tracking-tight">
                  {isNew ? "Tambah Alamat Baru" : "Edit Alamat"}
                </h1>
                <p className="text-slate-500 mt-1 text-sm lg:text-base">
                  Kelola alamat pengiriman Anda untuk memudahkan proses
                  checkout.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/location-address/my-address")}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-md font-medium text-slate-700 shadow-sm hover:bg-secondaryActive hover:border-secondary/30 hover:text-secondary transition-colors"
              >
                <IoArrowBack size={18} />
                Kembali
              </button>
            </div>
          </div>

          <div className="md:hidden">
            <div className="rounded-md p-3 w-full">
              <form onSubmit={formik.handleSubmit}>
                {step === 1 ? (
                  <>
                    <h1 className="font-semibold mb-2 text-lg text-slate-700">
                      Alamat Penerima
                    </h1>

                    <div className="space-y-4 mb-4">
                      <div>
                        <p className="mt-1 text-md text-slate-500">
                          Pastikan alamat di bawah ini (provinsi, kota,
                          kecamatan, kelurahan, dan kode pos) sudah diisi dengan
                          benar agar kurir dapat menemukan lokasi pengiriman
                          dengan tepat.
                        </p>
                      </div>

                      <div>
                        <p>
                          Provinsi <span className="text-red-500">*</span>
                        </p>
                        <Autocomplete
                          fullWidth
                          options={locationData.provinces}
                          getOptionLabel={(option) => option.name}
                          value={
                            locationData.provinces.find(
                              (p) => p.name === formik.values.province_name,
                            ) || null
                          }
                          onChange={handleProvinceChange}
                          onBlur={formik.handleBlur}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name="province_name"
                              placeholder="Pilih Provinsi"
                              error={
                                formik.touched.province_name &&
                                Boolean(formik.errors.province_name)
                              }
                              helperText={
                                formik.touched.province_name &&
                                formik.errors.province_name
                              }
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <InputAdornment
                                      position="start"
                                      sx={{ mr: { xs: 1.2, lg: 1.7 } }}
                                    >
                                      <LuMapPin
                                        size={18}
                                        className="text-gray-500 ml-1.5"
                                      />
                                    </InputAdornment>
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                                sx: {
                                  ...getFieldStyles(
                                    formik.errors.province_name &&
                                      formik.touched.province_name,
                                  ),
                                },
                              }}
                              FormHelperTextProps={{
                                sx: formHelperFieldStyles,
                              }}
                            />
                          )}
                        />
                      </div>

                      <div>
                        <p>
                          Kabupaten / Kota{" "}
                          <span className="text-red-500">*</span>
                        </p>
                        <Autocomplete
                          fullWidth
                          options={locationData.cities}
                          getOptionLabel={(option) => option.name}
                          value={
                            locationData.cities.find(
                              (p) => p.name === formik.values.city_name,
                            ) || null
                          }
                          onChange={handleCityChange}
                          onBlur={formik.handleBlur}
                          disabled={!formik.values.province_name}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name="city_name"
                              placeholder="Pilih Kabupaten / Kota"
                              error={
                                formik.touched.city_name &&
                                Boolean(formik.errors.city_name)
                              }
                              helperText={
                                formik.touched.city_name &&
                                formik.errors.city_name
                              }
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <InputAdornment
                                      position="start"
                                      sx={{ mr: { xs: 1.2, lg: 1.7 } }}
                                    >
                                      <LuMapPin
                                        size={18}
                                        className="text-gray-500 ml-1.5"
                                      />
                                    </InputAdornment>
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                                sx: {
                                  ...getFieldStyles(
                                    formik.errors.city_name &&
                                      formik.touched.city_name,
                                  ),
                                },
                              }}
                              FormHelperTextProps={{
                                sx: formHelperFieldStyles,
                              }}
                            />
                          )}
                        />
                      </div>

                      <div>
                        <p>
                          Kecamatan <span className="text-red-500">*</span>
                        </p>
                        <Autocomplete
                          fullWidth
                          options={locationData.districts}
                          getOptionLabel={(option) => option.name}
                          value={
                            locationData.districts.find(
                              (p) => p.name === formik.values.district_name,
                            ) || null
                          }
                          onChange={handleDistrictChange}
                          onBlur={formik.handleBlur}
                          disabled={!formik.values.city_name}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name="district_name"
                              placeholder="Pilih Kecamatan"
                              error={
                                formik.touched.district_name &&
                                Boolean(formik.errors.district_name)
                              }
                              helperText={
                                formik.touched.district_name &&
                                formik.errors.district_name
                              }
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <InputAdornment
                                      position="start"
                                      sx={{ mr: { xs: 1.2, lg: 1.7 } }}
                                    >
                                      <LuMapPin
                                        size={18}
                                        className="text-gray-500 ml-1.5"
                                      />
                                    </InputAdornment>
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                                sx: {
                                  ...getFieldStyles(
                                    formik.errors.district_name &&
                                      formik.touched.district_name,
                                  ),
                                },
                              }}
                              FormHelperTextProps={{
                                sx: formHelperFieldStyles,
                              }}
                            />
                          )}
                        />
                      </div>

                      <div>
                        <p>
                          Kelurahan <span className="text-red-500">*</span>
                        </p>
                        <Autocomplete
                          fullWidth
                          options={locationData.subDistricts}
                          getOptionLabel={(option) => option.name}
                          value={
                            locationData.subDistricts.find(
                              (p) => p.name === formik.values.sub_district_name,
                            ) || null
                          }
                          onChange={handleSubDistrictChange}
                          onBlur={formik.handleBlur}
                          disabled={!formik.values.district_name}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name="sub_district_name"
                              placeholder="Pilih Kelurahan"
                              error={
                                formik.touched.sub_district_name &&
                                Boolean(formik.errors.sub_district_name)
                              }
                              helperText={
                                formik.touched.sub_district_name &&
                                formik.errors.sub_district_name
                              }
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <InputAdornment
                                      position="start"
                                      sx={{ mr: { xs: 1.2, lg: 1.7 } }}
                                    >
                                      <LuMapPin
                                        size={18}
                                        className="text-gray-500 ml-1.5"
                                      />
                                    </InputAdornment>
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                                sx: {
                                  ...getFieldStyles(
                                    formik.errors.sub_district_name &&
                                      formik.touched.sub_district_name,
                                  ),
                                },
                              }}
                              FormHelperTextProps={{
                                sx: formHelperFieldStyles,
                              }}
                            />
                          )}
                        />
                      </div>

                      <div>
                        <p>
                          Kode Pos <span className="text-red-500">*</span>
                        </p>
                        <TextField
                          fullWidth
                          disabled
                          placeholder="Kode Pos akan terisi otomatis"
                          value={formik.values.postal_code}
                          onBlur={formik.handleBlur}
                          InputProps={{
                            sx: getFieldStyles(
                              formik.errors.postal_code &&
                                formik.touched.postal_code,
                            ),
                          }}
                          FormHelperTextProps={{
                            sx: formHelperFieldStyles,
                          }}
                        />
                      </div>

                      <div>
                        <p>
                          Alamat Lengkap <span className="text-error">*</span>
                        </p>
                        <TextField
                          fullWidth
                          placeholder="Contoh: Lobby Tower 1 Unit 10"
                          value={formik.values.detail_address}
                          onChange={(e) =>
                            formik.setFieldValue(
                              "detail_address",
                              e.target.value,
                            )
                          }
                          onBlur={formik.handleBlur}
                          name="detail_address"
                          helperText={
                            formik.touched.detail_address &&
                            formik.errors.detail_address
                          }
                          error={
                            formik.touched.detail_address &&
                            Boolean(formik.errors.detail_address)
                          }
                          InputProps={{
                            sx: getFieldStyles(
                              formik.errors.detail_address &&
                                formik.touched.detail_address,
                            ),
                          }}
                          FormHelperTextProps={{
                            sx: formHelperFieldStyles,
                          }}
                        />
                      </div>

                      <div>
                        <p>Catatan Pengiriman</p>
                        <TextField
                          fullWidth
                          placeholder="Masukkan catatan pengiriman"
                          value={formik.values.notes}
                          onChange={handleNotesChange}
                          onBlur={formik.handleBlur}
                          name="notes"
                          helperText={
                            formik.touched.notes && formik.errors.notes
                          }
                          error={
                            formik.touched.notes && Boolean(formik.errors.notes)
                          }
                          InputProps={{
                            sx: getFieldStyles(
                              formik.errors.notes && formik.touched.notes,
                            ),
                          }}
                          FormHelperTextProps={{
                            sx: formHelperFieldStyles,
                          }}
                        />

                        {noteSuggestions.length > 0 && (
                          <Box
                            sx={{
                              mt: 2,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            {noteSuggestions.map((suggestion, index) => {
                              const isSelected =
                                formik.values.notes === suggestion;

                              return (
                                <Chip
                                  key={index}
                                  label={suggestion}
                                  onClick={() =>
                                    handleSuggestionClick(suggestion)
                                  }
                                  variant={isSelected ? "filled" : "outlined"}
                                  color="primary"
                                  size="medium"
                                  sx={{
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    borderRadius: 4,
                                    fontFamily: '"Montserrat", sans-serif',
                                    ...(isSelected && {
                                      backgroundColor: "#94c679",
                                      color: "#fff",
                                      borderColor: "#94c679",
                                      "&:hover": {
                                        backgroundColor: "#94c679",
                                      },
                                    }),
                                    ...(!isSelected && {
                                      borderColor: "#94c679",
                                      color: "#94c679",
                                      backgroundColor: "transparent",
                                      "&:hover": {
                                        backgroundColor: "primary.light",
                                        color: "white",
                                      },
                                    }),
                                    transition: "all 0.2s ease",
                                  }}
                                />
                              );
                            })}
                          </Box>
                        )}
                      </div>
                    </div>

                    <h1 className="font-semibold mb-2 text-lg text-slate-700">
                      Informasi Penerima
                    </h1>

                    <div className="space-y-4">
                      <div>
                        <p>
                          Nama Lengkap <span className="text-error">*</span>
                        </p>
                        <TextField
                          fullWidth
                          placeholder="Masukkan nama lengkap"
                          value={formik.values.user_name}
                          onChange={(e) =>
                            formik.setFieldValue("user_name", e.target.value)
                          }
                          onBlur={formik.handleBlur}
                          name="user_name"
                          helperText={
                            formik.touched.user_name && formik.errors.user_name
                          }
                          error={
                            formik.touched.user_name &&
                            Boolean(formik.errors.user_name)
                          }
                          InputProps={{
                            sx: getFieldStyles(
                              formik.errors.user_name &&
                                formik.touched.user_name,
                            ),
                          }}
                          FormHelperTextProps={{
                            sx: formHelperFieldStyles,
                          }}
                        />
                      </div>

                      <div>
                        <p>
                          Nomor Ponsel <span className="text-error">*</span>
                        </p>
                        <TextField
                          fullWidth
                          placeholder="(+62) 8xxxxxxxxxx"
                          value={formik.values.phone_number}
                          onChange={(e) => handlePhoneChange(e.target.value)}
                          onBlur={formik.handleBlur}
                          name="phone_number"
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          helperText={
                            formik.touched.phone_number &&
                            formik.errors.phone_number
                          }
                          error={
                            formik.touched.phone_number &&
                            Boolean(formik.errors.phone_number)
                          }
                          InputProps={{
                            sx: getFieldStyles(
                              formik.errors.phone_number &&
                                formik.touched.phone_number,
                            ),
                          }}
                          FormHelperTextProps={{
                            sx: formHelperFieldStyles,
                          }}
                        />
                      </div>

                      <div>
                        <FormControl
                          error={
                            formik.touched.is_priority &&
                            Boolean(formik.errors.is_priority)
                          }
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formik.values.is_priority}
                                onChange={(e) =>
                                  formik.setFieldValue(
                                    "is_priority",
                                    e.target.checked,
                                  )
                                }
                                onBlur={formik.handleBlur}
                                name="is_priority"
                              />
                            }
                            label={
                              <span
                                style={{
                                  fontFamily: '"Montserrat", sans-serif',
                                  fontSize: "14px",
                                }}
                              >
                                Atur sebagai alamat utama
                              </span>
                            }
                            sx={{
                              alignItems: "flex-start",
                              "& .MuiCheckbox-root": {
                                paddingTop: "0px",
                              },
                            }}
                          />
                        </FormControl>
                      </div>

                      {errorMessage && (
                        <div className="w-full flex justify-end mt-2">
                          <div className="flex items-center gap-2 bg-red-100 py-1.5 px-2.5 rounded-md">
                            <IoInformationCircleOutline
                              className="text-error shrink-0"
                              size={12}
                            />
                            <p className="text-sm text-red-700">
                              {errorMessage}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div
                      className="text-md fixed md:hidden bottom-0 left-0 right-0 bg-white rounded-t-3xl z-[99] max-h-[70vh] flex flex-col shadow-2xl"
                      style={{
                        animation: "slideUp 0.3s ease-out",
                      }}
                    >
                      <div className="w-full rounded-t-3xl flex items-center gap-2 px-3 py-4 text-base">
                        <button
                          type="button"
                          onClick={() => navigate(-1)}
                          className="h-[40px] border border-secondary flex items-center justify-center gap-2 bg-white text-secondary w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryHover transform duration-200 hover:scale-100 disabled:bg-slate-400 disabled:cursor-default"
                        >
                          Batal
                        </button>
                        <button
                          type="button"
                          onClick={handleGoToStep2}
                          disabled={!isStep1Valid}
                          className="h-[40px] border border-secondary flex items-center justify-center bg-secondary text-white w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryHover transform duration-200 hover:scale-100 disabled:bg-slate-400 disabled:border-slate-400 disabled:cursor-default"
                        >
                          Lanjut
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <h1 className="font-semibold text-lg text-slate-700 mb-3">
                      Tentukan Titik Lokasi
                    </h1>
                    <p className="text-sm text-slate-500 mb-4">
                      Ketuk peta untuk menandai titik lokasi pengiriman yang
                      tepat.
                    </p>
                    <LocationAssign
                      formik={formik}
                      geoAddress={[
                        formik.values.sub_district_name,
                        formik.values.district_name,
                        formik.values.city_name,
                        formik.values.province_name,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    />
                    {formik.values.address !== "" && (
                      <div className="mb-4">
                        <p className="text-sm text-slate-600 mb-1">
                          Detail Alamat
                        </p>
                        <div className="border border-secondary bg-secondaryActive rounded-xl w-full flex p-3 gap-2">
                          <div className="">
                            <LuMapPin size={24} className="text-error" />
                          </div>
                          <p className="">{formik.values.address}</p>
                        </div>
                      </div>
                    )}
                    <div
                      className="text-md fixed md:hidden bottom-0 left-0 right-0 bg-white rounded-t-3xl z-[99] max-h-[70vh] flex flex-col"
                      style={{
                        animation: "slideUp 0.3s ease-out",
                      }}
                    >
                      <div className="w-full rounded-t-3xl flex items-center gap-2 px-3 py-4 text-base">
                        <button
                          type="button"
                          onClick={() => setStep(1)}
                          className="h-[40px] border border-secondary flex items-center justify-center gap-2 bg-white text-secondary w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryHover transform duration-200 hover:scale-100"
                        >
                          Kembali
                        </button>
                        <button
                          type="submit"
                          disabled={
                            isPending || !isStep2Valid || isStepTransitioning
                          }
                          className="h-[40px] border border-secondary flex items-center justify-center bg-secondary text-white w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryHover transform duration-200 hover:scale-100 disabled:bg-slate-400 disabled:cursor-default"
                        >
                          Simpan
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </form>
            </div>
          </div>
          <div className="hidden md:flex gap-4 items-start pb-10">
            <div className="flex-1 min-w-0">
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
                <form onSubmit={formik.handleSubmit}>
                  {step === 1 ? (
                    <>
                      <h2 className="text-xl font-semibold text-slate-800 mb-5">
                        Informasi Alamat
                      </h2>

                      <div className="grid grid-cols-2 gap-5">
                        <div className="col-span-2">
                          <p className="mb-1">
                            Nama Lengkap <span className="text-error">*</span>
                          </p>
                          <TextField
                            fullWidth
                            placeholder="Masukkan nama lengkap"
                            value={formik.values.user_name}
                            onChange={(e) =>
                              formik.setFieldValue("user_name", e.target.value)
                            }
                            InputProps={{
                              sx: getFieldStyles(
                                formik.errors.user_name &&
                                  formik.touched.user_name,
                              ),
                            }}
                          />
                        </div>

                        <div className="col-span-2">
                          <p className="mb-1">
                            Nomor Ponsel <span className="text-error">*</span>
                          </p>
                          <TextField
                            fullWidth
                            placeholder="(+62) 8xxxxxxxxxx"
                            value={formik.values.phone_number}
                            onChange={(e) => handlePhoneChange(e.target.value)}
                            InputProps={{
                              sx: getFieldStyles(
                                formik.errors.phone_number &&
                                  formik.touched.phone_number,
                              ),
                            }}
                          />
                        </div>

                        <div>
                          <p>
                            Provinsi <span className="text-red-500">*</span>
                          </p>
                          <Autocomplete
                            fullWidth
                            options={locationData.provinces}
                            getOptionLabel={(option) => option.name}
                            value={
                              locationData.provinces.find(
                                (p) => p.name === formik.values.province_name,
                              ) || null
                            }
                            onChange={handleProvinceChange}
                            onBlur={formik.handleBlur}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="province_name"
                                placeholder="Pilih Provinsi"
                                error={
                                  formik.touched.province_name &&
                                  Boolean(formik.errors.province_name)
                                }
                                helperText={
                                  formik.touched.province_name &&
                                  formik.errors.province_name
                                }
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <InputAdornment
                                        position="start"
                                        sx={{ mr: { xs: 1.2, lg: 1.7 } }}
                                      >
                                        <LuMapPin
                                          size={18}
                                          className="text-gray-500 ml-1.5"
                                        />
                                      </InputAdornment>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                  sx: {
                                    ...getFieldStyles(
                                      formik.errors.province_name &&
                                        formik.touched.province_name,
                                    ),
                                  },
                                }}
                                FormHelperTextProps={{
                                  sx: formHelperFieldStyles,
                                }}
                              />
                            )}
                          />
                        </div>

                        <div>
                          <p>
                            Kabupaten / Kota{" "}
                            <span className="text-red-500">*</span>
                          </p>
                          <Autocomplete
                            fullWidth
                            options={locationData.cities}
                            getOptionLabel={(option) => option.name}
                            value={
                              locationData.cities.find(
                                (p) => p.name === formik.values.city_name,
                              ) || null
                            }
                            onChange={handleCityChange}
                            onBlur={formik.handleBlur}
                            disabled={!formik.values.province_name}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="city_name"
                                placeholder="Pilih Kabupaten / Kota"
                                error={
                                  formik.touched.city_name &&
                                  Boolean(formik.errors.city_name)
                                }
                                helperText={
                                  formik.touched.city_name &&
                                  formik.errors.city_name
                                }
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <InputAdornment
                                        position="start"
                                        sx={{ mr: { xs: 1.2, lg: 1.7 } }}
                                      >
                                        <LuMapPin
                                          size={18}
                                          className="text-gray-500 ml-1.5"
                                        />
                                      </InputAdornment>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                  sx: {
                                    ...getFieldStyles(
                                      formik.errors.city_name &&
                                        formik.touched.city_name,
                                    ),
                                  },
                                }}
                                FormHelperTextProps={{
                                  sx: formHelperFieldStyles,
                                }}
                              />
                            )}
                          />
                        </div>

                        <div>
                          <p>
                            Kecamatan <span className="text-red-500">*</span>
                          </p>
                          <Autocomplete
                            fullWidth
                            options={locationData.districts}
                            getOptionLabel={(option) => option.name}
                            value={
                              locationData.districts.find(
                                (p) => p.name === formik.values.district_name,
                              ) || null
                            }
                            onChange={handleDistrictChange}
                            onBlur={formik.handleBlur}
                            disabled={!formik.values.city_name}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="district_name"
                                placeholder="Pilih Kecamatan"
                                error={
                                  formik.touched.district_name &&
                                  Boolean(formik.errors.district_name)
                                }
                                helperText={
                                  formik.touched.district_name &&
                                  formik.errors.district_name
                                }
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <InputAdornment
                                        position="start"
                                        sx={{ mr: { xs: 1.2, lg: 1.7 } }}
                                      >
                                        <LuMapPin
                                          size={18}
                                          className="text-gray-500 ml-1.5"
                                        />
                                      </InputAdornment>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                  sx: {
                                    ...getFieldStyles(
                                      formik.errors.district_name &&
                                        formik.touched.district_name,
                                    ),
                                  },
                                }}
                                FormHelperTextProps={{
                                  sx: formHelperFieldStyles,
                                }}
                              />
                            )}
                          />
                        </div>

                        <div>
                          <p>
                            Kelurahan <span className="text-red-500">*</span>
                          </p>
                          <Autocomplete
                            fullWidth
                            options={locationData.subDistricts}
                            getOptionLabel={(option) => option.name}
                            value={
                              locationData.subDistricts.find(
                                (p) =>
                                  p.name === formik.values.sub_district_name,
                              ) || null
                            }
                            onChange={handleSubDistrictChange}
                            onBlur={formik.handleBlur}
                            disabled={!formik.values.district_name}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="sub_district_name"
                                placeholder="Pilih Kelurahan"
                                error={
                                  formik.touched.sub_district_name &&
                                  Boolean(formik.errors.sub_district_name)
                                }
                                helperText={
                                  formik.touched.sub_district_name &&
                                  formik.errors.sub_district_name
                                }
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <InputAdornment
                                        position="start"
                                        sx={{ mr: { xs: 1.2, lg: 1.7 } }}
                                      >
                                        <LuMapPin
                                          size={18}
                                          className="text-gray-500 ml-1.5"
                                        />
                                      </InputAdornment>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                  sx: {
                                    ...getFieldStyles(
                                      formik.errors.sub_district_name &&
                                        formik.touched.sub_district_name,
                                    ),
                                  },
                                }}
                                FormHelperTextProps={{
                                  sx: formHelperFieldStyles,
                                }}
                              />
                            )}
                          />
                        </div>

                        <div className="col-span-2">
                          <p>
                            Kode Pos <span className="text-red-500">*</span>
                          </p>
                          <TextField
                            fullWidth
                            disabled
                            placeholder="Kode Pos akan terisi otomatis"
                            value={formik.values.postal_code}
                            onBlur={formik.handleBlur}
                            InputProps={{
                              sx: getFieldStyles(
                                formik.errors.postal_code &&
                                  formik.touched.postal_code,
                              ),
                            }}
                            FormHelperTextProps={{
                              sx: formHelperFieldStyles,
                            }}
                          />
                        </div>
                        <div className="col-span-2">
                          <p>
                            Alamat Lengkap <span className="text-error">*</span>
                          </p>
                          <TextField
                            fullWidth
                            placeholder="Contoh: Lobby Tower 1 Unit 10"
                            value={formik.values.detail_address}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "detail_address",
                                e.target.value,
                              )
                            }
                            onBlur={formik.handleBlur}
                            name="detail_address"
                            helperText={
                              formik.touched.detail_address &&
                              formik.errors.detail_address
                            }
                            error={
                              formik.touched.detail_address &&
                              Boolean(formik.errors.detail_address)
                            }
                            InputProps={{
                              sx: getFieldStyles(
                                formik.errors.detail_address &&
                                  formik.touched.detail_address,
                              ),
                            }}
                            FormHelperTextProps={{
                              sx: formHelperFieldStyles,
                            }}
                          />
                        </div>

                        <div className="col-span-2">
                          <p>Catatan Pengiriman</p>
                          <TextField
                            fullWidth
                            placeholder="Masukkan catatan pengiriman"
                            value={formik.values.notes}
                            onChange={handleNotesChange}
                            onBlur={formik.handleBlur}
                            name="notes"
                            helperText={
                              formik.touched.notes && formik.errors.notes
                            }
                            error={
                              formik.touched.notes &&
                              Boolean(formik.errors.notes)
                            }
                            InputProps={{
                              sx: getFieldStyles(
                                formik.errors.notes && formik.touched.notes,
                              ),
                            }}
                            FormHelperTextProps={{
                              sx: formHelperFieldStyles,
                            }}
                          />

                          {noteSuggestions.length > 0 && (
                            <Box
                              sx={{
                                mt: 2,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                              }}
                            >
                              {noteSuggestions.map((suggestion, index) => {
                                const isSelected =
                                  formik.values.notes === suggestion;

                                return (
                                  <Chip
                                    key={index}
                                    label={suggestion}
                                    onClick={() =>
                                      handleSuggestionClick(suggestion)
                                    }
                                    variant={isSelected ? "filled" : "outlined"}
                                    color="primary"
                                    size="medium"
                                    sx={{
                                      cursor: "pointer",
                                      borderRadius: 4,
                                      fontSize: "14px",
                                      fontFamily: '"Montserrat", sans-serif',
                                      ...(isSelected && {
                                        backgroundColor: "#94c679",
                                        color: "#fff",
                                        borderColor: "#94c679",
                                        "&:hover": {
                                          backgroundColor: "#94c679",
                                        },
                                      }),
                                      ...(!isSelected && {
                                        borderColor: "#94c679",
                                        color: "#94c679",
                                        backgroundColor: "transparent",
                                        "&:hover": {
                                          backgroundColor: "#eff6eb",
                                          color: "#071b82",
                                        },
                                      }),
                                      transition: "all 0.2s ease",
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          )}
                        </div>

                        <div className="col-span-2">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formik.values.is_priority}
                                onChange={(e) =>
                                  formik.setFieldValue(
                                    "is_priority",
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label="Atur sebagai alamat utama"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="">
                      <h2 className="text-xl font-semibold text-slate-800 mb-4">
                        Tentukan Titik Lokasi
                      </h2>

                      <LocationAssign
                        formik={formik}
                        geoAddress={[
                          formik.values.sub_district_name,
                          formik.values.district_name,
                          formik.values.city_name,
                          formik.values.province_name,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      />
                    </div>
                  )}
                </form>
              </div>
            </div>

            <aside className="w-[360px] shrink-0 sticky lg:top-[82px]">
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
                <h2 className="text-lg font-semibold text-slate-800 mb-5">
                  Langkah Pengisian
                </h2>

                <div className="space-y-4">
                  <div
                    className={`rounded-2xl p-4 border ${
                      step === 1
                        ? "border-secondary bg-secondaryActive"
                        : "border-slate-200"
                    }`}
                  >
                    <p className="font-semibold">1. Informasi Alamat</p>
                    <p className="text-md text-slate-500 mt-1">
                      Isi data penerima dan alamat lengkap.
                    </p>
                  </div>

                  <div
                    className={`rounded-2xl p-4 border ${
                      step === 2
                        ? "border-secondary bg-secondaryActive"
                        : "border-slate-200"
                    }`}
                  >
                    <p className="font-semibold">2. Titik Lokasi</p>
                    <p className="text-md text-slate-500 mt-1">
                      Tentukan pin lokasi di peta.
                    </p>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  {step === 1 ? (
                    <>
                      <button
                        type="button"
                        onClick={handleGoToStep2}
                        disabled={!isStep1Valid}
                        className="h-12 w-full rounded-xl bg-secondary text-white font-medium disabled:bg-slate-300"
                      >
                        Lanjut ke Lokasi
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="h-12 w-full rounded-xl border border-secondary text-secondary font-medium"
                      >
                        Kembali
                      </button>

                      <button
                        type="submit"
                        onClick={formik.handleSubmit}
                        disabled={!isStep2Valid || isPending}
                        className="h-12 w-full rounded-xl bg-secondary text-white font-medium disabled:bg-slate-300"
                      >
                        {isPending ? "Menyimpan..." : "Simpan Alamat"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
};
export default HOC(AddNewAddress, "required-auth");
